/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { navigate } from "gatsby";

import classNames from "classnames";
import posthog from "posthog-js";

import { gaBeginCheckout } from "@analytics/google";
import {
  closeCart,
  selectCartItems,
  selectIsCartOpen,
  selectOldTotalPrice,
  selectTotalPrice,
} from "@store/cart";

import { Button } from "@components/common";
import PaymentMethodsGallery from "@components/common/image/gallery/payment-methods/PaymentMethodsGallery";

import getUpsellItems from "@helpers/items/getUpsellItems";

import moment from "moment-business-days";

import getFreeGifts from "@helpers/items/getFreeGifts";

import ProgressBar from "@components/common/ProgressBar";
import CartItem from "./item/CartItem";
import UpsellCartItem from "./UpsellCartItem/UpsellCartItem";
import UpgradeButton from "./UpgradeButton";

const Cart = () => {
  const dispatch = useDispatch();

  const [timeLeft, setTimeLeft] = useState(15 * 60);

  const isCartOpen = useSelector(selectIsCartOpen);
  const cartItems = useSelector(selectCartItems);
  const totalPrice = useSelector(selectTotalPrice);
  const oldTotalPrice = useSelector(selectOldTotalPrice);
  const freeGifts = useMemo(() => getFreeGifts(), []);

  const [currentStep, setCurrentStep] = useState(0);
  const [remainingAmount, setRemainingAmount] = useState(0);

  const hasSubscriptionItem = useMemo(
    () => cartItems.some((ci) => ci.configuration.offer.name === "Monthly Subscription"),
    [cartItems]
  );

  const stepPrices = useMemo(() => {
    const basePrices = hasSubscriptionItem
      ? [0, ...freeGifts.map((fg) => fg.additionalDetails.from).filter(Boolean)]
      : [40, ...freeGifts.map((fg) => fg.additionalDetails.from).filter(Boolean)];

    return basePrices.sort((a, b) => (a || 0) - (b || 0));
  }, [freeGifts, hasSubscriptionItem]);

  const upsellItems = useMemo(
    () => getUpsellItems(cartItems.map((ci) => ci.item.id)).slice(0, 2),
    [cartItems]
  );

  const nextWorkingDay = useMemo(
    () => moment(moment().date(), "mm-dd-yyyy").nextBusinessDay().format("ddd, MMM Do"),
    []
  );

  useEffect(() => {
    if (typeof window !== "undefined" && typeof window.zE === "function") {
      if (isCartOpen) {
        window.zE("messenger", "hide");
      } else {
        window.zE("messenger", "show");
      }
    }
  }, [isCartOpen]);

  const handleCloseCart = useCallback(() => {
    dispatch(closeCart());
  }, [dispatch]);

  const handleProceedToCheckout = () => {
    dispatch(closeCart());

    if (typeof window !== "undefined") {
      posthog.capture("Start checkout", {
        cartItems,
      });
    }

    void navigate("/checkout");
  };

  useEffect(() => {
    document.addEventListener("click", handleCloseCart);

    return () => document.removeEventListener("click", handleCloseCart);
  }, [handleCloseCart]);

  useEffect(() => {
    if (isCartOpen) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }

    return () => document.body.classList.remove("modal-open");
  }, [isCartOpen]);

  useEffect(() => {
    if (!isCartOpen) return;

    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);

          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    // eslint-disable-next-line consistent-return
    return () => clearInterval(interval);
  }, [isCartOpen]);

  useEffect(() => {
    let newStep = 0;
    let remaining = 0;

    // For subscription items, start at step 1 (Joint Care Treat is already unlocked)
    if (hasSubscriptionItem) {
      newStep = 1;
    }

    for (let i = 0; i < stepPrices.length; i++) {
      const stepPrice = stepPrices[i];

      if (stepPrice !== undefined && stepPrice > totalPrice) {
        remaining = stepPrice - totalPrice;
        break;
      }

      newStep = i + 1;
    }

    setCurrentStep(newStep);
    setRemainingAmount(remaining);
  }, [totalPrice, hasSubscriptionItem, stepPrices]);

  const minutes = Math.floor(timeLeft / 60)
    .toString()
    .padStart(2, "0");
  const seconds = (timeLeft % 60).toString().padStart(2, "0");

  return (
    <React.Fragment>
      {isCartOpen && <div className="overlay" />}
      <div
        onClick={(e) => e.stopPropagation()}
        className={classNames("cart", { "--is-open": isCartOpen })}
      >
        <div className="cart-header">
          <h3 className="cart-header__title">Shopping Cart</h3>

          <Button
            className="close-btn"
            icon="cross"
            color="transparent"
            iconClassName="close-btn-icon"
            aria-label="close-cart"
            onClick={handleCloseCart}
          />
        </div>

        <React.Fragment>
          {/* <div className="cart-banner-free-shipping">
            <span>
              {hasSubscriptionItem ? (
                <React.Fragment>
                  <strong>Congrats!</strong> You qualified for <strong>FREE SHIPPING</strong>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  Switch to subscription for <strong>FREE SHIPPING</strong> + <strong>Gifts</strong>
                </React.Fragment>
              )}
            </span>
            <div
              className={`cart-banner-free-shipping-line ${
                !hasSubscriptionItem ? "incomplete" : ""
              }`}
            />
          </div>
         */}
          {hasSubscriptionItem && (
            <div className="cart-banner">
              <span>🔥 Free Shipping + Gifts</span>
            </div>
          )}
        </React.Fragment>

        <div className="cart-body">
          {cartItems.length > 0 && (
            <ProgressBar currentStep={currentStep} remainingAmount={+remainingAmount.toFixed(2)} />
          )}

          {cartItems.length > 0 && <UpgradeButton cartItems={cartItems} />}
          <div className="cart-body-content">
            {cartItems
              .slice()
              .sort(
                (a, b) => (b.configuration.price > 0 ? 1 : 0) - (a.configuration.price > 0 ? 1 : 0)
              )
              .map((item, index) => (
                <CartItem key={index} cartItem={item} />
              ))}
          </div>
          {upsellItems.length && cartItems.length ? (
            <div className="cart-body-upsell">
              <h3>You may also be interested in</h3>
              <div
                className={`cart-body-upsell-items upsell-scroll ${
                  upsellItems.length === 1 && "center"
                }`}
              >
                {upsellItems.map((item) => (
                  <UpsellCartItem key={item.id} item={item} />
                ))}
              </div>
            </div>
          ) : (
            ""
          )}
        </div>

        <div className="cart-footer">
          <div className="cart-footer__total">
            <span className="cart-footer__total-text">TOTAL:</span>
            <div>
              <span className="cart-footer__total-old-price">${oldTotalPrice}</span>
              <span className="cart-footer__total-price">${totalPrice}</span>
            </div>
          </div>

          <div className="cart-footer__timer-container">
            <div className="cart-footer__timer-wrapper">
              We can only reserve your order for
              <div className="cart-footer__timer">
                <div className="cart-footer__timer-min">{minutes}</div>:
                <div className="cart-footer__timer-sec">{seconds}</div>
              </div>
              <br />
              minutes due to high demand
            </div>
          </div>

          <Button
            disabled={cartItems.length === 0}
            onClick={handleProceedToCheckout}
            className="cart-footer__proceed-to-checkout"
          >
            Proceed To Checkout
          </Button>
          <div className="ships-by">
            <span className="ships-by-title">Ships by</span>
            <span className="date">{nextWorkingDay}</span>
          </div>
          <div className="cart-footer__payment-methods">
            <PaymentMethodsGallery className="cart-payment" />
          </div>
          <div className="list-item">
            <span className="benefits-test">
              <strong>60 Day</strong> Money Back Guarantee
            </span>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default Cart;
